import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CampusHeader from '../../../../components/feature/CampusHeader';
import Footer from '../../../../components/feature/Footer';
import { resetPasswordByCode } from '../../../../services/auth';

const ResetPasswordCodeNewPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const email = useMemo(() => params.get('email') || '', [params]);
  const code = useMemo(() => params.get('code') || '', [params]);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e: any) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    if (!email || !code) { setError('Missing email or code. Go back and request a new code.'); return; }
    if (!password || password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (!/\d/.test(password)) { setError('Password must contain at least 1 number.'); return; }
    if (!/[^A-Za-z0-9]/.test(password)) { setError('Password must contain at least 1 symbol.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }

    setLoading(true);
    try {
      const ok = await resetPasswordByCode(email, code, password);
      if (!ok) { setError('Failed to reset password.'); return; }
      setInfo('Password reset successful. Redirecting to sign-in…');
      setTimeout(() => navigate('/auth/sign-in'), 1200);
    } catch (err: any) {
      setError(err?.message || 'Error resetting password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <CampusHeader />
      <main className="max-w-md mx-auto px-4 py-12">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-4 text-center">Set New Password</h1>
          <p className="text-gray-600 mb-6 text-center">Enter a new password for your account.</p>
          <form onSubmit={submit} className="space-y-4">
            <div className="relative">
              <input
                type={show ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="New password"
                className="w-full px-3 py-2 pr-11 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
              <button type="button" tabIndex={-1} onClick={() => setShow((s) => !s)} className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 hover:text-gray-700" aria-label={show ? 'Hide password' : 'Show password'}>
                <i className={`ri-${show ? 'eye-off-line' : 'eye-line'} text-lg`}></i>
              </button>
            </div>
            <p className="text-xs text-gray-500 -mt-2">At least 8 characters, 1 number, and 1 symbol.</p>

            <div className="relative">
              <input
                type={showConfirm ? 'text' : 'password'}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Confirm new password"
                className="w-full px-3 py-2 pr-11 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
              <button type="button" tabIndex={-1} onClick={() => setShowConfirm((s) => !s)} className="absolute inset-y-0 right-0 px-3 flex items-center text-gray-500 hover:text-gray-700" aria-label={showConfirm ? 'Hide password' : 'Show password'}>
                <i className={`ri-${showConfirm ? 'eye-off-line' : 'eye-line'} text-lg`}></i>
              </button>
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}
            {info && !error && <p className="text-sm text-green-700">{info}</p>}

            <button type="submit" disabled={loading} className="w-full px-4 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-60">
              {loading ? 'Updating…' : 'Reset Password'}
            </button>
            <button type="button" onClick={() => navigate('/auth/sign-in')} className="w-full px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
              Back to Sign In
            </button>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ResetPasswordCodeNewPage;
